/**
 * createSerialQueue — generic promise-chain serialization queue.
 *
 * Mirrors the `updateQueueRef` promise-chain pattern in ProductDevDetail
 * (click-order field-update serialization): tasks run strictly in enqueue
 * order, one at a time. A rejection in one task never wedges the queue — the
 * internal chain always continues via a swallowed continuation — while the
 * promise returned to THAT task's caller still rejects, so its own
 * error-handling (toast, etc.) fires normally.
 *
 * Callers pass a `task` THUNK, not a pre-built value: the thunk is only
 * invoked once it reaches the front of the queue, so it can read whatever is
 * freshest (a cache, a ref) at that moment rather than whatever was current
 * when it was enqueued. This is the mechanism that closes cross-card
 * lost-update races on the product-dev `setVariants` full-replace mutation
 * (see `useProductDevMutations.setVariantsSerialized`).
 */
export interface SerialQueue {
  enqueue<T>(task: () => Promise<T>): Promise<T>;
}

export function createSerialQueue(): SerialQueue {
  let tail: Promise<unknown> = Promise.resolve();
  return {
    enqueue<T>(task: () => Promise<T>): Promise<T> {
      const run = tail.then(task);
      // The chain tail must never reject, or every subsequent enqueue would
      // resolve against a rejected promise and skip straight to its own
      // rejection without ever calling its task.
      tail = run.then(
        () => undefined,
        () => undefined,
      );
      return run;
    },
  };
}
